/*
 * Research mode: name a paper, pick it from a list, get an explainer video.
 *
 * Three calls mirroring the backend's split (app/routers/papers_api.py), each
 * costing something different:
 *
 *   search  — one arXiv query. Cheap, and called repeatedly as the user corrects
 *             a title, so it is the only one wired to typing.
 *   resolve — adds the licence, which costs a second request per paper. Only for
 *             the one the user actually picked.
 *   create  — downloads the PDF, reads it with a multimodal model, plans the
 *             beats and renders their assets. Slow (~30-60s) and the expensive one.
 *
 * `create` returns the same job_id/status shape a normal submission does, so
 * everything downstream — estimate, confirm, progress, playback — is the
 * ordinary video flow with no special casing.
 *
 * Note there is NO arXiv-id detection here. An earlier version of this file
 * sniffed the prompt box for a pasted link; research mode is an explicit choice
 * the user makes instead, so a prompt that merely mentions a paper stays a prompt.
 */
import { postJson, postMultipart } from '@/shared/api/apiClient';
import { normalizeStatus } from '@/shared/api/backendContract';
import type { ProcessingStatus } from '@/shared/types';

export const SEARCH_PAPERS_PATH = '/api/v1/papers/search';
export const RESOLVE_PAPER_PATH = '/api/v1/papers/resolve';
export const CREATE_PAPER_VIDEO_PATH = '/api/v1/papers/video';
export const UPLOAD_PAPER_PATH = '/api/v1/papers/upload';
export const PAPER_FROM_URL_PATH = '/api/v1/papers/from-url';

/* The backend caps at 10; this is only what we choose to render. */
export const MAX_RESULTS_SHOWN = 8;

export type PaperHit = {
  arxivId: string;
  title: string;
  authors: string[];
  summary: string;
  published: string;
  absUrl: string;
};

export type PaperSearch = {
  results: PaperHit[];
  /* Which corpus answered. arXiv holds preprints only, so an empty list means
     "not on arXiv" rather than "no such paper" — the UI has to say which. */
  source: string;
};

type SearchApiResponse = {
  results: Array<{
    arxiv_id: string;
    title: string;
    authors?: string[];
    summary?: string;
    published?: string;
    abs_url: string;
  }>;
  source?: string;
};

export async function searchPapers(query: string): Promise<PaperSearch> {
  const response = await postJson<SearchApiResponse>(SEARCH_PAPERS_PATH, { query });
  return {
    results: (response.results ?? []).slice(0, MAX_RESULTS_SHOWN).map((hit) => ({
      arxivId: hit.arxiv_id,
      title: hit.title,
      authors: hit.authors ?? [],
      summary: hit.summary ?? '',
      published: hit.published ?? '',
      absUrl: hit.abs_url,
    })),
    source: response.source ?? 'arxiv',
  };
}

export type PaperResolve = {
  arxivId: string;
  title: string;
  authors: string[];
  published: string;
  absUrl: string;
  licenseName: string;
  figuresRedistributable: boolean;
};

type PaperResolveApiResponse = {
  arxiv_id: string;
  title: string;
  authors: string[];
  published: string;
  abs_url: string;
  license: { name: string; figures_redistributable: boolean };
};

export async function resolvePaper(identifier: string): Promise<PaperResolve> {
  const response = await postJson<PaperResolveApiResponse>(RESOLVE_PAPER_PATH, {
    identifier,
  });
  return {
    arxivId: response.arxiv_id,
    title: response.title,
    authors: response.authors ?? [],
    published: response.published,
    absUrl: response.abs_url,
    licenseName: response.license?.name ?? 'Unknown licence',
    figuresRedistributable: Boolean(response.license?.figures_redistributable),
  };
}

type GenerateApiResponse = { job_id: string; status: string };

export type PaperVideoResult = { jobId: string; status: ProcessingStatus };

/*
 * Slow on purpose (~30-60s): the backend downloads the PDF, spends a model read,
 * plans the beats and renders their assets inside this request. Caller owns the
 * spinner.
 */
export async function createPaperVideo(
  identifier: string,
  signal?: AbortSignal,
): Promise<PaperVideoResult> {
  const response = await postJson<GenerateApiResponse>(CREATE_PAPER_VIDEO_PATH, {
    identifier,
  }, { signal });
  return { jobId: response.job_id, status: normalizeStatus(response.status) };
}

/* Authors as a byline: three names then "et al.", because a 30-author paper
   would otherwise push the title out of a candidate row. */
export function byline(authors: string[]): string {
  if (authors.length === 0) return '';
  if (authors.length <= 3) return authors.join(', ');
  return `${authors.slice(0, 3).join(', ')} et al.`;
}

/* arXiv returns a full ISO timestamp; a candidate row only needs the year. */
export function publishedYear(published: string): string {
  const match = published.match(/^(\d{4})/);
  return match ? match[1] : '';
}

/*
 * A PDF the user has, rather than one arXiv holds. Same pipeline server-side —
 * `analyze_paper` takes bytes and hands them to the model as a document part, so
 * the source is immaterial to the reading.
 *
 * This covers the papers a title search never can: anything published only in a
 * journal is absent from arXiv no matter how well we search it.
 *
 * One difference the UI should be honest about: figure excerpts are refused,
 * because an upload carries no licence and we cannot know whether the authors'
 * images may be reproduced. Everything redrawn from the numbers still works.
 */
export async function uploadPaper(
  file: File,
  signal?: AbortSignal,
): Promise<PaperVideoResult> {
  const form = new FormData();
  form.append('file', file, file.name);
  const response = await postMultipart<GenerateApiResponse>(UPLOAD_PAPER_PATH, form, {
    signal,
  });
  return { jobId: response.job_id, status: normalizeStatus(response.status) };
}

/*
 * A link to a PDF anywhere public. The SERVER downloads it, which is the one
 * place in this product where that happens — everywhere else a remote file is
 * fetched client-side so the API only receives uploads. It cannot be done here:
 * a cross-origin `fetch()` needs CORS headers and paper hosts do not send them.
 *
 * The engine carries the guard that makes it safe (app/papers/remote_pdf.py):
 * https only, every resolved address checked against private and link-local
 * space, the connection pinned to the validated IP, redirects rechecked per hop.
 * A refused link comes back as a 400 with a message written to be shown.
 */
export async function paperFromUrl(
  url: string,
  signal?: AbortSignal,
): Promise<PaperVideoResult> {
  const response = await postJson<GenerateApiResponse>(
    PAPER_FROM_URL_PATH,
    { url },
    { signal },
  );
  return { jobId: response.job_id, status: normalizeStatus(response.status) };
}

/*
 * Is this input a link to a PDF rather than a title or an arXiv reference?
 *
 * Deliberately crude, and deliberately NOT a second copy of the engine's arXiv
 * parsing: an arXiv link is handled by the engine's own identifier check, which
 * knows every form and settles the licence. This only has to answer "did the
 * user paste a web address that is not arXiv", so the engine's stricter guard
 * decides everything that matters.
 */
export function looksLikePdfUrl(text: string): boolean {
  const trimmed = text.trim();
  if (!/^https?:\/\//i.test(trimmed)) return false;
  if (/(^|\/\/)([a-z0-9-]+\.)*arxiv\.org\//i.test(trimmed)) return false;
  return true;
}

/**
 * A short name for a pasted PDF link, for the "Reading ..." heading.
 *
 * The other two submission paths name their subject -- the upload path shows the
 * filename, the candidate path shows the paper's title -- and this one showed the
 * constant "the linked PDF". That is the only path whose subject the user typed,
 * so it is the one where seeing it back matters most: a mistyped or wrong link
 * otherwise reads as a generic two-minute wait instead of as the wrong paper.
 *
 * The filename is the useful half of a PDF URL (`.../2404.08471v1.pdf`), so it is
 * preferred; a link with no filename segment (`/pdf?id=abc`) falls back to the
 * host. Anything unparseable falls back to the old constant rather than printing
 * a raw string of unknown length into a heading.
 */
/**
 * What is safe to put in a heading: no format or control characters, bounded length.
 *
 * `\p{C}` covers the `Cf` class, which includes the bidi overrides -- `%E2%80%AE` in a
 * filename decodes to RLO and reverses everything drawn after it. The length cap is not
 * cosmetic either: the CSS ellipsis bounds what is SEEN, and the accessible name still
 * carries the whole string, so a screen reader reads all of it.
 */
function headingSafe(text: string): string {
  return text.replace(/\p{C}/gu, '').slice(0, 80) || 'the linked PDF';
}

export function pdfUrlLabel(text: string): string {
  const trimmed = text.trim();
  try {
    const url = new URL(trimmed);
    const last = url.pathname.split('/').filter(Boolean).pop();
    if (last && /\.pdf$/i.test(last)) {
      try {
        return headingSafe(decodeURIComponent(last));
      } catch {
        /* `decodeURIComponent` throws on a percent that is not a valid escape,
           which a real filename can carry -- `50%off.pdf`. The raw segment is
           still the paper's name, so keep it rather than discarding a good
           label for the one path where naming the subject is the point. */
        return headingSafe(last);
      }
    }
    /* Empty for a URL with no authority: `mailto:` and every other non-special
       scheme parses rather than throwing, and an empty host renders the heading
       as "Reading " with nothing after it. */
    return headingSafe(url.host);
  } catch {
    return 'the linked PDF';
  }
}
